import { inject } from '@angular/core';
import { CookieDomainService } from '@services/cookie-domain.service';

export class CookieStorageProxy {
  private readonly cookieDomainService = inject(CookieDomainService);

  get cookieDomain(): string {
    return this.cookieDomainService.getCookieDomain();
  }

  // Prefix parameter is kept for interface compatibility with LocalStorageProxy
  // and to maintain the same constructor signature. Currently unused because
  // cookies need to match the exact name used by the Rails backend (see buildKey).
  constructor(public prefix: string) {}

  get(key: string): string | null {
    const name = this.buildKey(key);
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length);
      }
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
    }
    return null;
  }

  getJSON(key: string): any | null {
    const data = this.get(key);
    if (data) {
      try {
        return JSON.parse(data);
      } catch (e) {
        console.error('Error parsing JSON from cookie:', e);
        return null;
      }
    }
    return null;
  }

  setJSON(key: string, value: any, days = 365) {
    this.set(key, JSON.stringify(value), days);
  }

  set(key: string, value: string, days = 365) {
    const keyValue = `${this.buildKey(key)}=${encodeURIComponent(value)}`;
    const expires = this.getExpirationDate(days);

    this.setCookie(keyValue, expires);
  }

  remove(key: string) {
    const keyValue = `${this.buildKey(key)}=`;
    const expires = 'Thu, 01 Jan 1970 00:00:00 UTC';
    this.setCookie(keyValue, expires);
  }

  private setCookie(keyValue: string, expires: string) {
    const domain = this.cookieDomain;
    const domainAttribute = domain ? `;domain=${domain}` : '';

    let cookieString = keyValue;
    cookieString += `;expires=${expires}`;
    cookieString += `;path=/${domainAttribute}`;
    cookieString += `;SameSite=Lax`;

    // Adds the secure flag when running in production
    if (window.location.protocol === 'https:') {
      cookieString += ';Secure';
    }

    document.cookie = cookieString;
  }

  // We were prefixing the key, but now we need it with the same name as the cookie in the Rails app.
  private buildKey(key: string): string {
    // return `${this.prefix}-${key}`;
    return key;
  }

  private getExpirationDate(days: number): string {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    return date.toUTCString();
  }
}
